import classNames from 'classnames';
import Immutable from 'immutable';
import React from 'react';

import DropdownMenu from 'sharedreact/components/DropdownMenu';
import { translate } from 'sharedreact/utils/LanguageSupport';

type Props<T> = {
  className?: string,
  id: string,
  name: string,
  value: T | null,
  placeholder?: string,
  options: Immutable.List<T>,
  disabled?: boolean,
  showDoesNotExist?: boolean,
  alignRight?: boolean,

  onChangeValue: (name: string, option: T, index: number, event: React.MouseEvent<HTMLLIElement>) => void,
  setOptionClass?: (option: T, index: number) => string,
  formatOption?: (option: T, index?: number) => React.ReactNode,
  areValuesEqual?: (value1: T | undefined, value2: T | undefined) => boolean,
};

type DefaultProps = Required<Pick<Props<unknown>, 'showDoesNotExist'>>;

type PropsWithDefaults<T> = Omit<Props<T>, keyof DefaultProps> & DefaultProps;

class DropdownInput<T> extends React.PureComponent<Props<T>> {
  static defaultProps: DefaultProps = {
    showDoesNotExist: true
  };

  areEqual (value1: T | undefined, value2: T | undefined): boolean {
    const { areValuesEqual } = this.props;

    if (areValuesEqual != null) {
      return areValuesEqual(value1, value2);
    }

    if (Immutable.isImmutable(value1) || Immutable.isImmutable(value2)) {
      return Immutable.is(value1, value2);
    } else {
      return value1 === value2;
    }
  }

  override render (): React.ReactNode {
    const { id, name, value, setOptionClass, showDoesNotExist, placeholder,
      options, disabled, onChangeValue, formatOption, className,
      alignRight} = (this.props as PropsWithDefaults<T>);

    const hasOption: boolean = options.findIndex((option) => {
      return value != null && this.areEqual(option, value);
    }) !== -1;

    const content = !(value == null || value === '') || hasOption
                         ? (formatOption != null && value != null ? formatOption(value) : value?.toString())
                         : (placeholder == null || placeholder === '')
                            ? translate('shared.general.pleaseselect')
                            : placeholder;

    return (
      <DropdownMenu id={id}
        className={classNames(className, 'esko_form-control', {'esko_dropdown-doesnotexist': (showDoesNotExist && value && !hasOption)})}
        content={content}
        disabled={disabled}
        alignRight={alignRight}
      >
        {options.map((option, index) => {
          return (
            <li key={id + index}
              className={classNames({active: value != null && this.areEqual(option, value)})}
              onClick={(event: React.MouseEvent<HTMLLIElement>) => onChangeValue(name, option, index, event)}>
              <a role='button'
                className={setOptionClass != null ? setOptionClass(option, index) : undefined}
              >
                {formatOption != null ? formatOption(option, index) : (option as React.ReactNode)}
              </a>
            </li>
          );
        }).toArray()}
      </DropdownMenu>
    );
  }
}

export default DropdownInput;
